// pages/master-data/Eto/MasterGeofenceLibraryPage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Chip, Box, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { checkIsUsedInRouteTrip } from '../../../stores/routeTripStore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import { deleteGeofence } from '../../../stores/geofenceStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import MasterGeofenceLibraryAddEdit from './GeofenceLibraryAddEdit';
import { fleetApi } from '../../../api/axiosClient';
import { useLanguage } from '../../../utils/i18n';
import PolygonTypePage from '../polygon-type/PolygonTypePage';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

import { usePolygonTypeList } from '../../../stores/polygonTypeStore';

function MasterGeofenceLibraryPageContent() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const [selectedPolygonType, setSelectedPolygonType] = useState('all');

  const { data: polygonTypeData } = usePolygonTypeList(0, 1000, 'name', '');

  const polygonTypeFilters = useMemo(() => {
    const defaultTabs = [{ label: t('general.searchAll', 'All'), value: 'all' }];
    const fetchedTabs = (polygonTypeData || []).map((pt) => ({
      label: pt.name,
      value: String(pt.id)
    }));
    return [...defaultTabs, { label: t('geofenceList.unknownType', 'Unknown Type'), value: 'unknown' }, ...fetchedTabs];
  }, [polygonTypeData, t]);

  const [openPolygonTypePage, setOpenPolygonTypePage] = useState(false);
  const [filterPage, setFilterPage] = useState(0);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(polygonTypeFilters.length / itemsPerPage);

  useEffect(() => {
    if (filterPage >= totalPages && totalPages > 0) {
      setFilterPage(totalPages - 1);
    }
  }, [totalPages, filterPage]);

  const displayedFilters = polygonTypeFilters.slice(filterPage * itemsPerPage, (filterPage + 1) * itemsPerPage);

  const { companyOptions } = useCompanyList();
  const notify = useNotifier();

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('geofence.name', 'Geofence Name'), fieldName: 'name' },
      { value: 'geofence_code', label: t('geofence.code', 'Geofence Code'), fieldName: 'geofence_code' },
      { value: 'address', label: t('company.address', 'Address'), fieldName: 'address' },
      { value: 'remark', label: 'Remark' },
      { value: 'min_speed', label: t('geofenceList.minSpeed', 'Min Speed') },
      { value: 'max_speed', label: t('geofenceList.maxSpeed', 'Max Speed') },
      { value: 'polygon_type_name', label: t('geofenceList.polygonType', 'Polygon Type') }
    ],
    [t]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
        if (u?.rolename === 'Administrator' || u?.role_name === 'Administrator') {
          setIsAdmin(true);
        }
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('Auto select company failed', e);
    }
  }, [companyOptionsNormalized]);

  const baseFilters = useMemo(() => {
    let filters = [];
    let storedCompanyId = null;

    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (!hasAll) {
        const values = selectedCompanies.map((s) => String(s.value));
        if (values.length === 1) {
          filters.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
        } else {
          filters.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
        }
      }
    } else if (storedCompanyId && String(storedCompanyId) !== 'all') {
      filters.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) });
    }

    if (selectedPolygonType !== 'all') {
      if (selectedPolygonType === 'unknown') {
        const unknowType = [
          { name: 'polygon_type_id', logic_operator: 'IS NULL', operator: 'OR' },
          { name: 'polygon_type_id', logic_operator: '=', operator: 'OR', value: '0' }
        ];
        filters.push(...unknowType);
      } else {
        const selectedTab = polygonTypeFilters.find((pt) => pt.value === selectedPolygonType);
        if (selectedTab) {
          filters.push({
            name: 'polygon_type_id',
            logic_operator: '=',
            operator: 'AND',
            value: selectedTab.value
          });
        }
      }
    }

    return filters;
  }, [selectedCompanies, selectedPolygonType, polygonTypeFilters]);

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchGeofenceData = async () => {
      setIsLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        let searchColumns = [...baseFilters];
        let searchFilters = [];

        if (searchValue) {
          if (searchField === 'all') {
            searchFilters = [
              { name: 'name', value: searchValue },
              { name: 'geofence_code', value: searchValue },
              { name: 'address', value: searchValue },
              { name: 'min_speed', value: searchValue },
              { name: 'max_speed', value: searchValue },
              { name: 'remark', value: searchValue },
              { name: 'name', table_name: 'm_company', value: searchValue },
              { name: 'polygon_type_name', value: searchValue }
            ];
          } else {
            const selectedOpt = searchFieldOptions.find((o) => o.value === searchField);
            const searchCol = {
              name: selectedOpt?.fieldName || searchField,
              logic_operator: 'like',
              operator: 'AND',
              value: searchValue
            };
            if (searchField === 'polygon_type_name') {
              const searchPolyType = [
                { name: searchField, logic_operator: 'like', operator: 'OR', value: searchValue },
                { name: 'name', logic_operator: 'like', table_name: 'polygon_type', operator: 'OR', value: searchValue }
              ];
              searchColumns.push(...searchPolyType);
            } else {
              if (selectedOpt?.tableName) {
                searchCol.table_name = selectedOpt.tableName;
              }
              searchColumns.push(searchCol);
            }
          }
        }

        if (searchColumns.length === 0) {
          searchColumns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'polygon_type', column_results: ['name', 'label', 'description', 'fill_color', 'outline_color'] }
          ],
          columns: searchColumns,
          filters: searchFilters
        };

        const resp = await fleetApi.post('/geofence/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const resultRows = Array.isArray(data) ? data : (data?.data ?? []);
        const resultTotal = Array.isArray(data) ? resultRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setRows(resultRows);
        setTotalRecords(resultTotal);
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Geofence search error', err);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    fetchGeofenceData();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, baseFilters, searchFieldOptions]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      let searchColumns = [...baseFilters];
      let searchFilters = [];

      if (searchValue) {
        if (searchField === 'all') {
          searchFilters = [
            { name: 'name', value: searchValue },
            { name: 'geofence_code', value: searchValue },
            { name: 'address', value: searchValue },
            { name: 'min_speed', value: searchValue },
            { name: 'max_speed', value: searchValue },
            { name: 'remark', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'polygon_type_name', value: searchValue }
          ];
        } else {
          const selectedOpt = searchFieldOptions.find((o) => o.value === searchField);
          const searchCol = {
            name: selectedOpt?.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          };
          if (searchField === 'polygon_type_name') {
            const searchPolyType = [
              { name: searchField, logic_operator: 'like', operator: 'OR', value: searchValue },
              { name: 'name', logic_operator: 'like', table_name: 'polygon_type', operator: 'OR', value: searchValue }
            ];
            searchColumns.push(...searchPolyType);
          } else {
            if (selectedOpt?.tableName) {
              searchCol.table_name = selectedOpt.tableName;
            }
            searchColumns.push(searchCol);
          }
        }
      }

      if (searchColumns.length === 0) {
        searchColumns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'polygon_type', column_results: ['name', 'label', 'description', 'fill_color', 'outline_color'] }
        ],
        columns: searchColumns,
        filters: searchFilters
      };

      const resp = await fleetApi.post('/geofence/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      const resultRows = Array.isArray(data) ? data : (data?.data ?? []);
      return resultRows;
    } catch (err) {
      console.error('Geofence export fetch error', err);
      return [];
    }
  };

  const dynamicTitle =
    selectedPolygonType === 'all'
      ? t('geofenceList.title', 'Master Geofence Library')
      : polygonTypeFilters.find((pt) => pt.value === selectedPolygonType)?.label || t('geofenceList.unknownType', 'Unknown Type');

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1 && sanitized[0].value !== 'all') {
          localStorage.setItem('companyId', String(sanitized[0].value));
        } else {
          localStorage.removeItem('companyId');
        }
      }
    } catch {
      console.log('eror');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const handleDelete = async (id, itemName) => {
    const { isUsed, routeNames } = await checkIsUsedInRouteTrip(id, 'geofence');
    if (isUsed) {
      setWarningMessage(
        <Box>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Data "<strong>{itemName}</strong>" is not allowed to be deleted because it is registered in the following route(s):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, maxHeight: 150, overflowY: 'auto', p: 0.5 }}>
            {routeNames.map((r, i) => (
              <Chip key={i} label={r} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, bgcolor: 'error.50' }} />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            If you wish to delete it, please update the route first.
          </Typography>
        </Box>
      );
      setWarningOpen(true);
      return;
    }

    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteGeofence(confirmTargetId);
      notify({ message: t('geofence.notifSuccess', 'Geofence successfully deleted'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('geofence.notifFailed', 'Failed to delete Geofence'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ field, value }) => {
    const safeField = typeof field === 'object' ? field?.value : field;
    setSearchField(safeField || 'all');
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1.2,
      valueGetter: (params, row) => row?.join?.company_name || '-'
    },
    { field: 'name', headerName: t('geofence.name', 'Geofence Name'), flex: 2 },
    { field: 'geofence_code', headerName: t('geofence.code', 'Geofence Code'), flex: 1 },
    { field: 'address', headerName: t('company.address', 'Address'), flex: 2 },
    {
      field: 'polygon_type_name',
      headerName: t('polygonType.title', 'Polygon Type'),
      flex: 1,
      renderCell: (params) => {
        const text = params.row?.polygon_type_name || params.row?.join?.polygon_type_name || '-';
        return <Chip label={text} color="primary" variant="outlined" size="small" sx={{ textTransform: 'capitalize' }} />;
      }
    },
    {
      field: 'remark',
      headerName: t('geofenceList.remark', 'Remark'),
      flex: 1
    },
    {
      field: 'min_speed',
      headerName: t('geofenceList.minSpeed', 'Min Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.min_speed ? `${params.min_speed} km/h` : '-')
    },
    {
      field: 'max_speed',
      headerName: t('geofenceList.maxSpeed', 'Max Speed'),
      flex: 1,
      valueGetter: (value, params) => (params.max_speed ? `${params.max_speed} km/h` : '-')
    },
    {
      field: 'status',
      headerName: t('geofenceList.status', 'Status'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      valueGetter: (value, row) => {
        const st = row?.status ?? row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Active' : 'Not Active';
        return String(st);
      },
      renderCell: (params) => {
        const isActive = params.row?.status ?? params.row?.is_active ?? false;
        return isActive ? (
          <Chip label={t('general.active', 'Active')} color="success" variant="outlined" size="small" />
        ) : (
          <Chip label={t('general.notActive', 'Not Active')} color="error" variant="outlined" size="small" />
        );
      }
    },
    { field: 'geom', headerName: t('polygonType.geom', 'Geom'), hide: true },
    { field: 'centroid_lat', headerName: t('polygonType.centroidLatitude', 'Centroid Latitude'), hide: true },
    { field: 'centroid_lon', headerName: t('polygonType.centroidLongitude', 'Centroid Longitude'), hide: true },

    ...(isAdmin ||
    (typeof window !== 'undefined' &&
      (localStorage.getItem('rolename') === 'Administrator' || localStorage.getItem('role_name') === 'Administrator'))
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      {/* === MENU FILTER POLYGON TYPE === */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton disabled={filterPage === 0} onClick={() => setFilterPage((p) => p - 1)} size="small" sx={{ mr: 1 }}>
            <ChevronLeftIcon />
          </IconButton>
          <Stack direction="row" spacing={1} flexWrap="nowrap">
            {displayedFilters.map((filter) => (
              <Chip
                key={filter.value}
                label={filter.label}
                onClick={() => {
                  setSelectedPolygonType(filter.value);
                  setPaginationModel((p) => ({ ...p, page: 0 }));
                }}
                color={selectedPolygonType === filter.value ? 'primary' : 'default'}
                variant={selectedPolygonType === filter.value ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: selectedPolygonType === filter.value ? 'bold' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: selectedPolygonType === filter.value ? 'primary.main' : 'rgba(0,0,0,0.05)'
                  }
                }}
              />
            ))}
          </Stack>
          <IconButton disabled={filterPage >= totalPages - 1} onClick={() => setFilterPage((p) => p + 1)} size="small" sx={{ ml: 1 }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {isAdmin && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenPolygonTypePage(true)}>
            {t('geofenceList.addPolygonType', 'Add Polygon Type')}
          </Button>
        )}
      </Box>

      <DataTable
        title={dynamicTitle}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          // Sanitasi field untuk mencegah value [object Object]
          const safeField = typeof f === 'object' ? f?.value : f;
          setSearchField(safeField || 'all');
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.searchByName', 'Search by Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      <MasterGeofenceLibraryAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptionsNormalized}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
          handleDialogClose();
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.operationFailed', 'Operation failed'), severity: 'error' });
        }}
      />

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0, overflow: 'visible', boxShadow: '0 10px 30px rgba(8,14,30,0.12)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
                border: '1px solid rgba(255,152,0,0.18)'
              }}
            >
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Action Denied</DialogTitle>
          </Box>
          <IconButton onClick={() => setWarningOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {typeof warningMessage === 'string' ? (
            <Typography variant="body1" sx={{ mb: 1.25 }}>
              {warningMessage}
            </Typography>
          ) : (
            warningMessage
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setWarningOpen(false);
              navigate('/master-route');
            }}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600
            }}
          >
            Master Route
          </Button>
          <Button
            variant="contained"
            onClick={() => setWarningOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />

      <Dialog fullWidth maxWidth="lg" open={openPolygonTypePage} onClose={() => setOpenPolygonTypePage(false)}>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {t('geofenceList.polygonType', 'Polygon Type')}
          <IconButton
            onClick={() => setOpenPolygonTypePage(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, minHeight: '60vh' }}>
          <PolygonTypePage />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MasterGeofenceLibraryPage() {
  return (
    <>
      <MasterGeofenceLibraryPageContent />
    </>
  );
}
